import type { FilterFieldDef } from 'components/common/MasterFilter/smartFilterTypes';
import { FAIL_REASON_FILTER_OPTIONS } from 'utils/failReasonLabels';
import { LEAD_SUB_ORIGINS } from 'utils/leadOrigins';
import { PARTNER_ORIGIN_LABELS, PARTNER_ORIGINS } from 'utils/partnerCampaigns';

const leadsFilterFields: FilterFieldDef[] = [
  // ─── Dates ───
  { backendField: 'createdAt', label: 'Creación', type: 'dateRange', group: 'Fechas' },
  { backendField: 'CRMUpdateAt', label: 'Fecha Subida CRM', type: 'dateRange', group: 'Fechas' },
  {
    backendField: 'CRMUpdateAt',
    id: 'CRMUpdateAt:exists',
    label: 'Subido a CRM (sí/no)',
    type: 'existence',
    group: 'Fechas',
  },
  { backendField: 'visitDate', label: 'Fecha de visita', type: 'dateRange', group: 'Fechas' },
  {
    backendField: 'createdAtOutsideWorkingHours',
    label: 'Fuera de horario laboral',
    type: 'boolean',
    group: 'Fechas',
  },

  // ─── Status & enums ───
  {
    backendField: 'status',
    label: 'Estado',
    type: 'enum',
    group: 'Estado',
    options: [
      { value: 'CREATED', label: 'Creado' },
      { value: 'HANDLED_BY_AI', label: 'IA' },
      { value: 'INTERESTING_FOR_SALESMAN', label: 'Interesante' },
      { value: 'ASSIGNED_TO_SALESMAN', label: 'Asignado' },
      { value: 'PENDING_TO_BE_HANDLED', label: 'Pendiente' },
      { value: 'PENDING_TO_BE_ASSIGNED', label: 'Por asignar' },
      { value: 'HANDLED_BY_SALESMAN', label: 'Manejando' },
      { value: 'CLOSED', label: 'Cerrado' },
    ],
  },
  { backendField: 'source', label: 'Origen', type: 'enum', dynamicOptionsKey: 'sources', group: 'Estado' },
  {
    backendField: 'partner',
    label: 'Alianza',
    type: 'enum',
    group: 'Origen',
    options: PARTNER_ORIGINS.map((origin) => ({ value: origin, label: PARTNER_ORIGIN_LABELS[origin] })),
    visibleWhen: (ctx) => ctx.businessUnitType === 'financial_institution',
  },
  {
    backendField: 'origin',
    label: 'Categoría',
    type: 'enum',
    dynamicOptionsKey: 'origins',
    group: 'Origen',
  },
  {
    backendField: 'subOrigin',
    label: 'Suborigen',
    type: 'enum',
    group: 'Origen',
    options: LEAD_SUB_ORIGINS.map((s) => ({ value: s, label: s })),
  },
  {
    backendField: 'whatsAppCampaign',
    label: 'Campaña Outbound',
    type: 'enum',
    dynamicOptionsKey: 'whatsAppCampaigns',
    group: 'Estado',
  },

  // ─── Booleans ───
  { backendField: 'discarded', label: 'Descartado', type: 'boolean', group: 'Estado' },
  { backendField: 'isTestLead', label: 'Lead de prueba', type: 'boolean', group: 'Estado' },
  {
    backendField: 'clientDisabledAutomaticResponses',
    label: 'Resp. automáticas deshabilitadas',
    type: 'boolean',
    group: 'Estado',
  },
  {
    backendField: 'clientDisabledFollowUps',
    label: 'Follow ups/campañas deshabilitadas',
    type: 'boolean',
    group: 'Estado',
  },

  // ─── People ───
  {
    backendField: 'clientUserId',
    label: 'Ejecutivo asignado',
    type: 'userReference',
    dynamicOptionsKey: 'assignedUser',
    group: 'Personas',
  },

  // ─── Text search ───
  { backendField: 'client.name', label: 'Nombre cliente', type: 'textSearch', group: 'Texto' },
  { backendField: 'client.phone', label: 'Teléfono', type: 'textSearch', group: 'Texto' },
  { backendField: 'client.rut', label: 'RUT', type: 'textSearch', group: 'Texto' },
  { backendField: 'client.notes', label: 'Notas cliente', type: 'textSearch', group: 'Texto' },
  { backendField: 'metaCampaignName', label: 'Campaña Meta', type: 'textSearch', group: 'Texto' },
  { backendField: 'metaAdName', label: 'Nombre de anuncio', type: 'textSearch', group: 'Texto' },

  // Deep-link-only filters by the exact Meta asset IDs (from the campaign results panel).
  // Hidden from the manual picker but still render as removable chips when active.
  {
    backendField: 'metaCampaignExternalId',
    label: 'Campaña Meta (ID)',
    type: 'textSearch',
    group: 'Texto',
    visibleWhen: () => false,
  },
  {
    backendField: 'metaAdSetExternalId',
    label: 'Conjunto de anuncios Meta (ID)',
    type: 'textSearch',
    group: 'Texto',
    visibleWhen: () => false,
  },
  {
    backendField: 'metaAdExternalId',
    label: 'Anuncio Meta (ID)',
    type: 'textSearch',
    group: 'Texto',
    visibleWhen: () => false,
  },

  // ─── Numeric ───
  { backendField: 'id', label: 'Filtro por id', type: 'numberList', group: 'Métricas' },
  { backendField: 'messagesCount', label: 'Mensajes', type: 'numericRange', group: 'Métricas' },
  { backendField: 'clientMessagesCount', label: 'Mensajes cliente', type: 'numericRange', group: 'Métricas' },
  { backendField: 'failedMessagesCount', label: 'Mensajes fallidos', type: 'numericRange', group: 'Métricas' },
  {
    backendField: 'failReason',
    label: 'Motivo de fallo',
    type: 'enum',
    group: 'Métricas',
    options: FAIL_REASON_FILTER_OPTIONS,
  },
  { backendField: 'templatesSentCount', label: 'Plantillas enviadas', type: 'numericRange', group: 'Métricas' },
  {
    backendField: 'advancePercentageBucket',
    label: 'Avance venta',
    type: 'enum',
    group: 'Métricas',
    options: [
      { value: '0-10', label: '0–10%' },
      { value: '10-20', label: '10–20%' },
      { value: '20-30', label: '20–30%' },
      { value: '30-40', label: '30–40%' },
      { value: '40-50', label: '40–50%' },
      { value: '50-60', label: '50–60%' },
      { value: '60-70', label: '60–70%' },
      { value: '70-80', label: '70–80%' },
      { value: '80-90', label: '80–90%' },
      { value: '90-100', label: '90–100%' },
    ],
    visibleWhen: (ctx) => ctx.businessUnitId === 39,
  },

  // ─── Car dealership only ───
  {
    backendField: 'currentBrand',
    label: 'Marca interés',
    type: 'enum',
    dynamicOptionsKey: 'currentBrands',
    group: 'Vehículos',
    visibleWhen: (ctx) => ctx.businessUnitType === 'car_dealership',
  },
  {
    backendField: 'initialBrandOfInterest',
    label: 'Marca inicial',
    type: 'enum',
    dynamicOptionsKey: 'brands',
    group: 'Vehículos',
    visibleWhen: (ctx) => ctx.businessUnitType === 'car_dealership',
  },
  {
    backendField: 'initialModelOfInterest',
    label: 'Modelo interés',
    type: 'textSearch',
    group: 'Vehículos',
    visibleWhen: (ctx) => ctx.businessUnitType === 'car_dealership',
  },
  {
    backendField: 'branchOffice',
    label: 'Sucursal',
    type: 'enum',
    dynamicOptionsKey: 'branchOffices',
    group: 'Vehículos',
    visibleWhen: (ctx) => ctx.businessUnitType === 'car_dealership',
  },
  {
    backendField: 'endReason',
    label: 'Motivo de término',
    type: 'enum',
    dynamicOptionsKey: 'endReasons',
    group: 'Estado',
    visibleWhen: (ctx) => ctx.businessUnitType === 'car_dealership' || ctx.businessUnitType === 'insurance_broker',
  },
  {
    backendField: 'creditStatus',
    label: 'Estado crédito',
    type: 'enum',
    dynamicOptionsKey: 'creditStatuses',
    group: 'Estado',
    visibleWhen: (ctx) => ctx.businessUnitType === 'car_dealership' || ctx.businessUnitType === 'financial_institution',
  },

  {
    backendField: 'bankOfferStatus',
    label: 'Oferta bancaria',
    type: 'enum',
    group: 'Estado',
    // Cumulative: matches requests that reached the state at any point in their history, so the
    // counts line up with the dashboard funnel instead of the current-status snapshot.
    // `pre_approved_pending` is the exception — it matches the current status, mirroring the
    // dashboard's commercial-backlog card.
    options: [
      { value: 'created', label: 'Solicitud creada' },
      { value: 'pre_approved', label: 'Pre-aprobada' },
      { value: 'pre_approved_pending', label: 'Pre-aprobada sin enviar' },
      { value: 'sent_to_bank', label: 'Enviada al banco' },
      { value: 'rejected_by_bank', label: 'Rechazada por el banco' },
    ],
    visibleWhen: (ctx) => ctx.businessUnitType === 'financial_institution',
  },

  // ─── Tags (topics) ───
  {
    backendField: 'topics',
    label: 'Tags',
    type: 'enum',
    dynamicOptionsKey: 'topics',
    group: 'Estado',
  },
];

export default leadsFilterFields;
